import React, { useEffect, useState } from "react";
import { Pagination } from "./Pagination";

interface TableProps {
  columnNames: string[];
  columnKeys: string[];
  data: any[];
  ITEMS_PER_PAGE: number;
  hyperlinkColumns?: { [columnKey: string]: string }; // Optional map of columnKey to URL key
  hyperlinkText?: string; // Optional static text for hyperlinks
}

const PaginationTable: React.FC<TableProps> = ({
  columnNames,
  columnKeys,
  data,
  ITEMS_PER_PAGE,
  hyperlinkColumns = {}, // Default empty object (no hyperlinks)
  hyperlinkText = "Click Here", // Default static text for hyperlink
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState<any[]>([]);

  // Calculate total pages - ensure at least 1 page even when empty
  const totalPages = Math.max(1, Math.ceil(data.length / ITEMS_PER_PAGE));

  const handlePageChange = React.useCallback(
    (newPage: number) => {
      if (newPage > 0 && newPage <= totalPages) {
        setCurrentPage(newPage);
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });

        // Only slice data if it's not empty
        if (data.length > 0) {
          const startIndex = (newPage - 1) * ITEMS_PER_PAGE;
          const endIndex = startIndex + ITEMS_PER_PAGE;
          setTableData(data.slice(startIndex, endIndex));
        } else {
          setTableData([]);
        }
      }
    },
    [totalPages, ITEMS_PER_PAGE, data]
  );

  useEffect(() => {
    // Reset to first page when data changes
    handlePageChange(1);
  }, [data, handlePageChange]);

  const renderTableCell = (item: any, columnKey: string) => {
    if (hyperlinkColumns[columnKey]) {
      // If the column is meant to display a hyperlink
      const url = item[hyperlinkColumns[columnKey]]; // Get the URL from the data
      return (
        <div className="">
          <a
            href={url}
            className="text-primary font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            {hyperlinkText} {/* Static link text */}
          </a>
        </div>
      );
    }

    // Default behavior for non-hyperlink cells
    return (
      <div className="text-[#8C8A96] text-[12px]">
        {typeof item[columnKey] === "number"
          ? item[columnKey] % 1 === 0
            ? item[columnKey]
            : item[columnKey].toFixed(2)
          : item[columnKey]}
      </div>
    );
  };

  return (
    <div className="table-container bg-white rounded-xl">
      {/* Table Header */}
      <div className="table-contentHeading w-full">
        {columnNames.map((columnName, index) => (
          <div key={index} className="column-heading w-full">
            {columnName}
          </div>
        ))}
      </div>

      {/* Table Rows */}
      <div className="elemDetailsSec">
        {tableData.length > 0 ? (
          tableData.map((item, index) => (
            <div className="tableDetails border-b" key={index}>
              {columnKeys.map((columnKey, index) => (
                <div key={index} className="table-cell w-full text-center">
                  {renderTableCell(item, columnKey)}
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="py-8 text-center text-gray-500">
            No data available
          </div>
        )}
      </div>

      {/* Only show pagination if there's data */}
      {data.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={data.length}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default PaginationTable;
